import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, BRAND, toEmbedUrl } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ImpactCounter } from "@/components/ImpactCounter";
import { Sprout, BookHeart, Sun, HeartPulse, PartyPopper, Baby, FileDown, ArrowRight, Ribbon, HeartCrack, Brain, Stethoscope, HeartHandshake } from "lucide-react";

const ICONS = { Sprout, BookHeart, Sun, HeartPulse, PartyPopper, Baby, FileDown, Ribbon, HeartCrack, Brain, Stethoscope, HeartHandshake };
const HERO = "https://images.unsplash.com/photo-1674620213535-9b2a2553ef40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const HOPE = "https://images.unsplash.com/photo-1568909555141-aebfc970e8d0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [collections, setCollections] = useState([]);
  const [video, setVideo] = useState("");
  const [families, setFamilies] = useState(0);
  const [kits, setKits] = useState(0);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 4)));
    api.get("/collections").then((r) => setCollections(r.data));
    api.get("/settings").then((r) => { setVideo(r.data.welcome_video_url || ""); setFamilies(r.data.impact_total ?? r.data.impact_families ?? 0); });
    api.get("/impact/kits").then((r) => setKits(r.data.kits_sent || 0)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-24 grid lg:grid-cols-[42%_58%] gap-12 lg:gap-16 items-center">
          <div className="gh-fade-up lg:pr-8">
            <span data-testid="hero-motto" className="block whitespace-nowrap font-heading italic text-[#8E9A86] text-sm sm:text-xl md:text-2xl">&ldquo;Helping People Feel Seen, Encouraged &amp; Supported.&rdquo;</span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.04] text-[#3A3632] mt-6">
              Comfort for life's<br /><span className="italic text-[#8E9A86]">hardest moments.</span>
            </h1>
            <div className="gh-hairline my-8 max-w-[120px]" />
            <p className="text-lg text-[#6B655F] max-w-md leading-relaxed font-light" data-testid="hero-subhead">
              Thoughtfully curated gift boxes for cancer, grief, anxiety, depression, recovery, caregiving, and encouragement. Each one finished by hand with a signature Hope Card.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary">Shop the Care Boxes <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/build-your-own-box" data-testid="hero-byob-btn" className="btn-outline">Build Your Own Box</Link>
            </div>
            <Link to="/kit-finder" data-testid="hero-quiz-link" className="inline-flex items-center gap-2 mt-5 text-sm text-[#8E9A86] gh-link-underline">
              <span aria-hidden>✨</span> Not sure which kit to choose? Take our 60-second Kit Finder Quiz
            </Link>
          </div>
          <div className="relative gh-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="gh-frame overflow-hidden">
              <img src={HERO} alt="An elegant gift box tied with care" className="w-full h-[560px] object-cover" />
              <div className="absolute inset-0 bg-[#FAF7F2] mix-blend-soft-light opacity-40 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#8A8175]/15 to-transparent pointer-events-none" />
            </div>
            <div className="absolute -bottom-7 -left-3 sm:left-8 bg-[#FAF7F2] border border-[#D9CBA0]/60 p-6 max-w-[250px] shadow-[0_18px_50px_rgba(26,26,26,0.10)]">
              <span className="gh-overline text-[10px]">The Hope Card</span>
              <p className="font-hand text-2xl text-[#3A3632] leading-tight mt-2">"We are so grateful you are here."</p>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial ribbon */}
      <div className="bg-[#8E9A86]/10 border-y border-[#D9CBA0]/40 py-5 overflow-hidden">
        <div className="flex gh-marquee whitespace-nowrap">
          {[...Array(2)].map((_, k) => (
            <div key={`marquee-loop-${k}`} className="flex items-center gap-32 pr-32">
              {["Helping People Feel Seen","Encouraged & Supported","A Handwritten Hope Card in Every Box","Kindness Over Extravagance","Sent With Gratitude"].map((t) => (
                <span key={t} className="font-heading text-2xl text-[#3A3632] italic">{t}</span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Mission statement */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-24 text-center" data-testid="mission-section">
        <span className="gh-overline">Our mission</span>
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#3A3632] mt-5 leading-tight">Comfort Delivered, Hope Shared.</h2>
        <div className="gh-hairline my-7 max-w-[100px] mx-auto" />
        <p className="text-lg text-[#6B655F] leading-relaxed font-light">
          At Hope &amp; Favor, we create thoughtfully curated care packages that help people express love, encouragement, and compassion during life's most difficult moments. Whether someone is facing illness, grief, anxiety, depression, or another challenging season, every gift is designed to remind them they are seen, supported, and never alone.
        </p>
      </section>

      {/* Impact counter */}
      <ImpactCounter target={families} kits={kits} />

      {/* Who we serve / Perfect for */}
      <section className="bg-[#3A3632] py-24" data-testid="who-we-serve">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <span className="gh-overline text-[#D9CBA0]">Who we serve</span>
          <h2 className="font-heading text-3xl sm:text-4xl text-[#FAF7F2] mt-5">Because sometimes flowers aren't enough.</h2>
          <p className="text-[#D9CBA0]/90 mt-6 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto font-light">
            Perfect for family members, friends, churches, employers, counselors, oncology teams, hospice, and caregivers — anyone who wants to say <span className="italic">"I'm here"</span> when words fall short.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            {["Family & Friends", "Churches", "Employers", "Counselors", "Oncology Teams", "Hospice", "Caregivers"].map((w) => (
              <span key={w} className="text-[13px] tracking-wide text-[#FAF7F2] border border-[#D9CBA0]/40 rounded-full px-4 py-2">{w}</span>
            ))}
          </div>
          <Link to="/corporate-care" data-testid="who-programs-btn" className="inline-block mt-11 border border-[#D9CBA0] text-[#D9CBA0] rounded-full px-8 py-3 font-medium hover:bg-[#D9CBA0] hover:text-[#3A3632] transition-colors">Explore our Care Programs</Link>
        </div>
      </section>

      {/* Collections */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-24">
        <div className="max-w-xl">
          <span className="gh-overline">Find the right words</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-4">Shop by collection</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#D9CBA0]/30 border border-[#D9CBA0]/30 mt-12">
          {collections.map((c, i) => {
            const Icon = ICONS[c.icon] || Sprout;
            return (
              <Link key={c.key} to={`/shop?collection=${c.key}`} data-testid={`collection-${c.key}`}
                className="group bg-[#FAF7F2] p-10 transition-colors duration-500 hover:bg-white gh-fade-up"
                style={{ animationDelay: `${i * 50}ms` }}>
                <div className="h-14 w-14 gh-octagon bg-[#8E9A86]/12 flex items-center justify-center mb-6 group-hover:bg-[#8E9A86] transition-colors duration-500">
                  <Icon className="h-6 w-6 text-[#8E9A86] group-hover:text-white transition-colors duration-500" />
                </div>
                <h3 className="font-heading text-2xl text-[#3A3632]">{c.name}</h3>
                <p className="text-sm text-[#6B655F] mt-2 leading-relaxed">{c.tagline}</p>
                <span className="gh-overline text-[10px] inline-block mt-5 group-hover:text-[#8E9A86] transition-colors">Explore →</span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="bg-[#F2EFEB] py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="gh-overline">Lovingly chosen</span>
              <h2 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-4">Favorite boxes</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline gh-link-underline text-[#8E9A86]" data-testid="featured-viewall">View all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((p, i) => <ProductCard key={p.id} p={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* Welcome video */}
      {toEmbedUrl(video) && (
        <section className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-24" data-testid="home-video-section">
          <div className="text-center max-w-xl mx-auto mb-10">
            <span className="gh-overline">A warm welcome</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-4">Watch our story</h2>
          </div>
          <div className="gh-frame overflow-hidden">
            <div className="aspect-video bg-[#1A1A1A]">
              <iframe src={toEmbedUrl(video)} title="Hope & Favor welcome video" className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </section>
      )}

      {/* Hope Card moment */}
      <section className="bg-[#C4A4A4]/10 py-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <span className="gh-overline">The signature touch</span>
          <h2 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-4 mb-10">Every order includes a Hope Card</h2>
          <div className="flex justify-center mb-10">
            <div className="gh-octagon w-44 h-44 sm:w-52 sm:h-52 overflow-hidden ring-1 ring-[#D9CBA0] p-1 bg-[#D9CBA0]/40">
              <img src={HOPE} alt="A handwritten Hope Card" className="gh-octagon w-full h-full object-cover" />
            </div>
          </div>
          <div className="bg-[#FAF7F2] border border-[#D9CBA0]/50 p-10 sm:p-14 max-w-3xl mx-auto">
            <p className="font-hand text-3xl sm:text-4xl text-[#3A3632] leading-relaxed">
              Today... take one deep breath. Give yourself grace. Celebrate every small victory. Remember that you are loved. May today bring peace to your heart and hope to your journey.
            </p>
            <div className="gh-hairline my-7 max-w-[100px] mx-auto" />
            <p className="font-hand text-2xl text-[#C4A4A4]">With gratitude, Hope &amp; Favor</p>
          </div>
          <Link to="/shop" data-testid="hopecard-shop-btn" className="btn-primary mt-12">Send a little hope</Link>
        </div>
      </section>

      {/* Hope Cards collectible promo */}
      <section className="bg-[#F2EFEB] py-24" data-testid="home-hope-cards-promo">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden border border-[#D9CBA0]/50 shadow-sm bg-white gh-fade-up max-w-sm mx-auto lg:mx-0 lg:ml-auto">
            <img
              src="https://customer-assets.emergentagent.com/job_hope-boxes/artifacts/1jsrfp9m_GIFT%20-%20H%26F%20HOPE%20CARD.png"
              alt="Hope & Favor Hope Card shown open with an encouraging message, Bible verse, QR code, and a removable golden tree-of-life heart keepsake charm."
              className="w-full h-auto"
              loading="lazy"
            />
          </div>
          <div className="text-center lg:text-left">
            <span className="gh-overline">Hope &amp; Favor&trade; · More than a card</span>
            <h2 className="font-heading text-4xl sm:text-5xl text-[#3A3632] mt-4 leading-tight">Keepsake Hope Cards</h2>
            <p className="font-hand text-2xl text-[#C4A4A4] mt-3">A reminder of hope they'll keep close.</p>
            <p className="text-lg text-[#6B655F] leading-relaxed font-light mt-5">
              Our Hope &amp; Favor&trade; Keepsake Hope Cards open to an encouraging message and a Bible verse, and includes a removable keepsake charm — worn, hung, or kept close as a daily reminder that someone cares. Each one links to a special QR message just for them.
            </p>
            <p className="font-hand text-2xl text-[#C4A4A4] mt-5 leading-snug">
              Every Hope Card is designed to remind someone they are loved, seen, and never alone.
            </p>
            <Link to="/hope-cards" data-testid="home-hope-cards-btn" className="btn-primary mt-9 inline-flex">Explore Keepsake Hope Cards <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
